<script setup>
import { nextTick, ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import OptionSelect from 'dashboard/components-next/select/OptionSelect.vue';
import EmojiInput from 'shared/components/emoji/EmojiInput.vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  showMessageType: {
    type: Boolean,
    default: true,
  },
  inputLabel: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const textareaRef = ref(null);
const showEmojiPicker = ref(false);
const emojiTrigger = '😊';

const messageTypeOptions = [
  {
    key: 'text',
    label: 'Text',
  },
  {
    key: 'media',
    label: 'Media',
    disabled: true,
  },
];

const insertEmoji = emoji => {
  const textarea = textareaRef.value;

  if (!textarea) {
    emit('update:modelValue', `${props.modelValue || ''}${emoji}`);
    showEmojiPicker.value = false;
    return;
  }

  const start = textarea.selectionStart ?? props.modelValue.length;
  const end = textarea.selectionEnd ?? props.modelValue.length;
  const value = props.modelValue || '';

  emit(
    'update:modelValue',
    `${value.slice(0, start)}${emoji}${value.slice(end)}`
  );

  nextTick(() => {
    const nextCursor = start + emoji.length;
    textarea.focus();
    textarea.setSelectionRange(nextCursor, nextCursor);
  });

  showEmojiPicker.value = false;
};
</script>

<template>
  <div class="min-w-0 flex flex-col gap-4">
    <div v-if="showMessageType" class="flex flex-col gap-1">
      <label class="text-xs font-medium leading-4 text-n-slate-11">
        {{ $t('AUTOMATION.ACTION.SEND_MESSAGE.MESSAGE_TYPE_LABEL') }}
      </label>
      <OptionSelect
        model-value="text"
        :options="messageTypeOptions"
        dropdown-max-height="max-h-40"
        :disabled="disabled"
      />
    </div>

    <label
      v-if="inputLabel"
      class="text-xs font-medium leading-4 text-n-slate-11"
    >
      {{ inputLabel }}
    </label>
    <div
      v-on-click-outside="() => (showEmojiPicker = false)"
      class="relative min-w-0 rounded-lg border border-n-weak bg-transparent transition-colors hover:border-n-slate-7 focus-within:border-n-brand dark:border-n-strong dark:hover:border-n-slate-7 dark:focus-within:border-n-brand"
    >
      <textarea
        ref="textareaRef"
        :value="modelValue"
        rows="3"
        dir="auto"
        :disabled="disabled"
        :placeholder="$t('AUTOMATION.ACTION.TEAM_MESSAGE_INPUT_PLACEHOLDER')"
        class="reset-base box-border min-h-[6.5rem] w-full max-w-full appearance-none resize-none rounded-none border-0 bg-transparent px-3 py-3 pb-12 text-sm leading-5 text-n-slate-12 shadow-none outline-none ring-0 placeholder:text-n-slate-10 hover:border-0 hover:shadow-none hover:outline-none hover:ring-0 focus:border-0 focus:shadow-none focus:outline-none focus:ring-0 focus-visible:outline-none disabled:cursor-not-allowed"
        @input="$emit('update:modelValue', $event.target.value)"
      />

      <div class="absolute bottom-2 left-2">
        <EmojiInput
          v-if="showEmojiPicker"
          :on-click="insertEmoji"
          placement="below-anchor-start"
        />
        <button
          type="button"
          class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="disabled"
          @click.stop="showEmojiPicker = !showEmojiPicker"
        >
          <span class="text-[22px] leading-none" aria-hidden="true">{{
            emojiTrigger
          }}</span>
        </button>
      </div>
    </div>
  </div>
</template>
